import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table";
import Table from "../../../ui/Table/Table";
import styles from "./Connections.module.scss";
import Button from "../../../ui/Button/Button";
import toSentenceCase from "../../../util/toSentenceCase";
import {
  LogoIconComponentType,
  Shopify,
  Webflow,
  Webhook,
  Wordpress,
  Zapier,
  Wix,
  Framer,
} from "../../../ui/LogoIcon/LogoIcon";
import Badge from "../../../ui/Badge/Badge";
import { graphql } from "../../../__generated__/gql";
import { useQuery } from "urql";
import urls from "../../../routes";
import { useNavigate } from "react-router-dom";
import SectionHeading from "../../../layout/SectionHeading/SectionHeading";
import useFeatureFlag from "../../../providers/FeatureFlagProvider/useFeatureFlag";

const ConnectionsTableQuery = graphql(/* GraphQL */ `
  query connectionsTable {
    canPublish {
      canPublishToZapier
      canPublishToWordpress
      canPublishToWebflow
      canPublishToWebhook
      canPublishToShopify
      canPublishToWix
      canPublishToFramer
    }
  }
`);

type ConnectionStatus = "CONNECTED" | "NOT_CONNECTED";

type ConnectionRow = {
  name:
    | "WEBFLOW"
    | "WORDPRESS"
    | "SHOPIFY"
    | "ZAPIER"
    | "WEBHOOK"
    | "WIX"
    | "FRAMER";
  status: ConnectionStatus | null;
};

const columnHelper = createColumnHelper<ConnectionRow>();

const LogoIcon: Record<ConnectionRow["name"], LogoIconComponentType> = {
  WEBFLOW: Webflow,
  WORDPRESS: Wordpress,
  SHOPIFY: Shopify,
  ZAPIER: Zapier,
  WEBHOOK: Webhook,
  WIX: Wix,
  FRAMER: Framer,
};

function ConnectionNameCell({ row }: CellContext<ConnectionRow, unknown>) {
  const Logo = LogoIcon[row.original.name];
  return (
    <div className={styles.nameCell}>
      <Logo size="xxlarge" />
      <span className={styles.name}>
        {row.original.name === "FRAMER"
          ? "Framer (Sync)"
          : toSentenceCase(row.original.name)}
      </span>
    </div>
  );
}

const connectionStatusLabel: Record<ConnectionStatus, string> = {
  CONNECTED: "Connected",
  NOT_CONNECTED: "Not connected",
};

const connectionUrl: Record<ConnectionRow["name"], string> = {
  WEBFLOW: urls.publishing.connections.webflow,
  WORDPRESS: urls.publishing.connections.wordpress,
  SHOPIFY: urls.publishing.connections.shopify,
  ZAPIER: urls.publishing.connections.zapier,
  WEBHOOK: urls.publishing.connections.webhook,
  WIX: urls.publishing.connections.wix,
  FRAMER: urls.publishing.connections.framer,
};

function ConnectionStatusCell({ row }: CellContext<ConnectionRow, unknown>) {
  if (!row.original.status) {
    return null;
  }

  return (
    <Badge color={row.original.status === "CONNECTED" ? "success" : "default"}>
      {connectionStatusLabel[row.original.status]}
    </Badge>
  );
}

function ConnectionButtonCell({ row }: CellContext<ConnectionRow, unknown>) {
  const navigate = useNavigate();

  return (
    <Button
      disabled={!row.original.status}
      variant="outline"
      onClick={() => navigate(connectionUrl[row.original.name])}
    >
      {row.original.status === "CONNECTED" ? "Manage" : "Connect"}
    </Button>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<ConnectionRow, any>[] = [
  columnHelper.accessor("name", {
    header: "Connection",
    cell: ConnectionNameCell,
  }),
  columnHelper.accessor("status", {
    header: "Status",
    size: 165,
    cell: ConnectionStatusCell,
  }),
  columnHelper.display({
    id: "button",
    size: 131,
    cell: ConnectionButtonCell,
  }),
];

const getStatus = (canPublish?: boolean) => {
  if (canPublish === undefined) {
    return null;
  }

  return canPublish ? "CONNECTED" : "NOT_CONNECTED";
};

function Connections() {
  const [connectionsTableResponse] = useQuery({ query: ConnectionsTableQuery });
  const { featureFlags } = useFeatureFlag();

  const data: ConnectionRow[] = [
    {
      name: "WEBFLOW",
      status: getStatus(
        connectionsTableResponse.data?.canPublish.canPublishToWebflow,
      ),
    },
    {
      name: "WORDPRESS",
      status: getStatus(
        connectionsTableResponse.data?.canPublish.canPublishToWordpress,
      ),
    },
    {
      name: "SHOPIFY",
      status: getStatus(
        connectionsTableResponse.data?.canPublish.canPublishToShopify,
      ),
    },
    {
      name: "WIX",
      status: getStatus(
        connectionsTableResponse.data?.canPublish.canPublishToWix,
      ),
    },
    {
      name: "ZAPIER",
      status: getStatus(
        connectionsTableResponse.data?.canPublish.canPublishToZapier,
      ),
    },
    ...(featureFlags.framerPlugin
      ? [
          {
            name: "FRAMER",
            status: getStatus(
              connectionsTableResponse.data?.canPublish.canPublishToFramer,
            ),
          } as const,
        ]
      : []),
    {
      name: "WEBHOOK",
      status: getStatus(
        connectionsTableResponse.data?.canPublish.canPublishToWebhook,
      ),
    },
  ];

  return (
    <Table
      Header={
        <SectionHeading
          className={styles.header}
          title="Connections"
          description="When an article is published it will be sent to all connected publishing integrations."
        />
      }
      Footer={
        <div>
          <Button
            variant="outline"
            onClick={() => window.Intercom("showNewMessage")}
          >
            Request a new integration
          </Button>
        </div>
      }
      columns={columns}
      data={data}
    />
  );
}

export default Connections;
